import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getBlockDetails,
  getBlockDimensions,
  getIndicatorTableData,
  resetMultiDimFilter,
  setFilterItems,
  setResetFilter,
  UpdatePlangPageIndicatorTableData,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import OutlineActionButton from '../../components/OutlineActionButton';
import BuilderModeSvgIcon from '../../icons/BuilderModeSvgIcon';

const SettingsPopoverContent = PopoverContent as React.ElementType;
const DimensionSwitch = Switch as React.ElementType;
const SettingsTooltip = Tooltip as React.ElementType;

export interface InputPanelDimensionSettingsV1Properties {
  indicatorId: number;
  refetchRequest: () => void;
}

const InputPanelDimensionSettingsV1 = memo(
  ({ indicatorId, refetchRequest }: InputPanelDimensionSettingsV1Properties) => {
    const dispatch = useDispatch<AppDispatch>();
    const indicatorTableData = useSelector(getIndicatorTableData);
    const baseScenario = useSelector(getBaseScenario);
    const dimensionList = useSelector(getBlockDetails);
    const dimensionItemList = useSelector(getBlockDimensions);
    const [dimensionData, setDimensionData] = useState(dimensionList?.dimensions || []);
    const [activeDim, setActiveDim] = useState(1);

    useEffect(() => {
      setDimensionData(dimensionList?.dimensions);
    }, [dimensionList?.dimensions]);

    useEffect(() => {
      const active = indicatorTableData?.dimensions?.length
        ? Number(indicatorTableData.dimensions.length) + 1
        : 1;
      setActiveDim(active);
    }, [indicatorTableData]);

    const doesDimensionExist = (
      dim: { id?: number; name?: string },
      dimArray: { id?: string }[],
    ) => {
      if (dim?.name?.toLowerCase() === 'time') return true;
      return dimArray?.some((data) => Number(data?.id) === dim?.id);
    };

    const addDim = (currentDim: { id: string }[], newDim: { id: number }) => {
      let found = false;
      const updatedDim = JSON.parse(JSON.stringify(currentDim)) as { id: string }[];
      updatedDim.forEach((dim, index) => {
        if (dim.id === String(newDim.id)) {
          updatedDim.splice(index, 1);
          found = true;
        }
      });
      if (!found) {
        updatedDim.push({ id: String(newDim.id) });
      }
      return updatedDim;
    };

    const handleSettingChange = async (data: { id: number }) => {
      const dim = indicatorTableData?.dimensions;
      const result = addDim(dim, data);
      const payload = {
        indicator_id: indicatorId,
        request_body: {
          dimensions: result,
          bulk_input: true,
          type: indicatorTableData?.type,
          format_excel_data: true,
          scenario_id: baseScenario?.id,
        },
      };
      await dispatch(resetMultiDimFilter());
      await dispatch(setFilterItems([]));
      await dispatch(setResetFilter(true));
      await dispatch(UpdatePlangPageIndicatorTableData(payload));
      await refetchRequest();
    };

    const hasDimensionItems = (id: number) => {
      const dimension = dimensionItemList?.dimensions?.find((dim: { id: number }) => dim.id === id);
      return Boolean(dimension?.items?.length);
    };

    const renderMenuList = (list: { id: number; name: string }[] | undefined) => (
      <Box maxH='190px' overflowY='auto' className='scroll'>
        {list?.map((data) => (
          <Flex key={data.id} align='center' py='8px'>
            <DimensionSwitch
              mr={2}
              disabled={
                data?.name === 'time' || data?.name === 'Time' || !hasDimensionItems(data?.id)
              }
              isChecked={doesDimensionExist(data, indicatorTableData?.dimensions)}
              onChange={() => handleSettingChange(data)}
            />
            {!hasDimensionItems(data?.id) ? (
              data?.name === 'time' || data?.name === 'Time' ? (
                <Text fontSize='sm'>{data?.name}</Text>
              ) : (
                <SettingsTooltip label='Dimension has no items'>
                  <Text fontSize='sm'>{data?.name}</Text>
                </SettingsTooltip>
              )
            ) : (
              <Text fontSize='sm'>{data?.name}</Text>
            )}
          </Flex>
        ))}
      </Box>
    );

    return (
      <Popover closeOnBlur placement='bottom-end'>
        <PopoverTrigger>
          <Box as='span' display='inline-flex'>
            <OutlineActionButton
              aria-label='Block dimensions'
              label={`(${activeDim}/${dimensionData?.length || 0})`}
              leftIcon={<BuilderModeSvgIcon name='listTree' />}
              minW='auto'
            />
          </Box>
        </PopoverTrigger>
        <SettingsPopoverContent bg='white' maxH='250px' w='230px' borderRadius='10px'>
          <Box px={4} py={2} color='black'>
            {renderMenuList(dimensionData)}
          </Box>
        </SettingsPopoverContent>
      </Popover>
    );
  },
);

InputPanelDimensionSettingsV1.displayName = 'InputPanelDimensionSettingsV1';

export default InputPanelDimensionSettingsV1;
